/**
 * The audio companion's state machine: who this browser is (a claimed code, or a renewal), the socket,
 * and the engine — wired together so the apps only draw. It runs ONCE for the whole tab, in
 * `AudioCompanionProvider` above every route (plan §9.11: the engine, its socket and its service worker
 * belong to the Rotom Phone's shell, so opening Stats or the Map never interrupts a track).
 *
 *   checking ──▶ claiming ──▶ ready ◀── (a renewal answered)
 *       │            │          │
 *       └────────────┴──▶ signed-out ◀── (the renewal went away mid-session)
 *
 * THE SOCKET OPENS AS SOON AS THE PAGE KNOWS WHO IT IS, before "Enable audio": connecting makes no
 * sound, so the page can already say "In game · Battle Isle" and the click only has to start the music.
 *
 * THE CLAIM HAPPENS ONCE PER CODE, whatever React does. A code is single-use, and React's strict mode
 * runs effects twice in development; a second claim would spend nothing but would fail, and racing it
 * against the renewal check would sign the page out. So the fragment is read once into state, stripped
 * from the address bar at once (a credential in the URL ends up in screenshots), and claimed through a
 * module-level once-per-code promise.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  isStaff,
  normaliseAudioCatalog,
  normalisePlayerAudioContext,
  normaliseInstanceRef,
  preferredAudioFormat,
  voiceLanguage,
  warmOrder,
  type AudioCatalog,
  type AudioContextMessage,
  type AudioServerMessage,
  type AudioVolumes,
} from '@dimensionscore/shared';

import { useSession } from '../auth/SessionProvider';
import { AudioChannel, type AudioChannelStatus } from './channel';
import { AudioEngine, IDLE_SNAPSHOT, type AudioBackend, type EngineSnapshot } from './engine';
import { LIBRARY, retainedUrls } from './library';
import { clearRenewalHint, hasRenewalHint, readAudioPrefs, writeAudioPrefs, writePlayerHint, writeRenewalHint, type AudioPrefs } from './prefs';
import { codeFromFragment, type AudioIdentity, type AudioSessionClient, type ClaimOutcome } from './session';
import { WebAudioBackend } from './webaudio';

export type CompanionPhase = 'checking' | 'claiming' | 'signed-out' | 'ready';

/** Why the page is signed out, worded for the person reading it. Null for "never was signed in". */
export type SignedOutReason = 'used_or_expired' | 'renewal_ended' | 'unreachable' | null;

export interface CompanionState {
  readonly phase: CompanionPhase;
  readonly identity: AudioIdentity | null;
  readonly signedOutReason: SignedOutReason;
  readonly link: AudioChannelStatus;
  /** A context has arrived since the socket last opened — the game server is speaking. */
  readonly heard: boolean;
  readonly catalog: AudioCatalog | null;
  readonly context: AudioContextMessage | null;
  readonly snapshot: EngineSnapshot;
  readonly enabled: boolean;
  /** Pressed "Enable audio" before, here — the button says "Resume" rather than explaining itself again. */
  readonly enabledBefore: boolean;
  readonly reloadRequired: boolean;
  readonly lastCue: { readonly id: string; readonly at: number } | null;
  readonly prefs: AudioPrefs;
  readonly supported: boolean;
}

const claims = new Map<string, Promise<ClaimOutcome>>();
function claimOnce(client: AudioSessionClient, code: string): Promise<ClaimOutcome> {
  let pending = claims.get(code);
  if (pending === undefined) {
    pending = client.claim(code);
    claims.set(code, pending);
  }
  return pending;
}

export interface CompanionOptions {
  /** Injected by tests: the engine's sound. Web Audio in a browser that has it. */
  readonly backendFactory?: () => AudioBackend;
}

/**
 * The machine itself, for `AudioCompanionProvider` alone — apps use its `useAudioCompanion`.
 *
 * `active` GATES THE WAY IN. Until an app that plays or shows the music has been opened (the Music app,
 * the home screen, the map), nothing is claimed, renewed or dialled: a staff member in the console, or a
 * player reading Stats, costs the sidecar nothing. Once active it stays active for the tab's life.
 */
export function useCompanionMachine(options: CompanionOptions & { readonly active: boolean }) {
  const active = options.active;
  const { session, adoptSession, adoptSelf, audio, signOut: signOutOfEverything, signOuts } = useSession();
  const [code] = useState<string | null>(() => codeFromFragment(globalThis.location?.hash ?? ''));
  const [phase, setPhase] = useState<CompanionPhase>(code === null ? 'checking' : 'claiming');
  const [identity, setIdentity] = useState<AudioIdentity | null>(null);
  const [signedOutReason, setSignedOutReason] = useState<SignedOutReason>(null);
  const [link, setLink] = useState<AudioChannelStatus>('idle');
  const [heard, setHeard] = useState(false);
  const [catalog, setCatalog] = useState<AudioCatalog | null>(null);
  const [context, setContext] = useState<AudioContextMessage | null>(null);
  const [snapshot, setSnapshot] = useState<EngineSnapshot>(IDLE_SNAPSHOT);
  const [enabled, setEnabled] = useState(false);
  const [reloadRequired, setReloadRequired] = useState(false);
  const [lastCue, setLastCue] = useState<CompanionState['lastCue']>(null);
  const [prefs, setPrefs] = useState<AudioPrefs>(() => readAudioPrefs());

  const supported = useMemo(() => options.backendFactory !== undefined || WebAudioBackend.supported(), [options.backendFactory]);
  // A player-tier session from Discord can stand in for a renewal (the sidecar then leaves one behind).
  const bearerRef = useRef<string | null>(null);
  bearerRef.current = session !== null && session.claims.role === 'player' ? session.token : null;

  const channelRef = useRef<AudioChannel | null>(null);
  const tickSink = useRef<((sample: { positionMs: number; expectedMs: number | null; atMs: number; durationMs: number | null }) => void) | null>(null);

  // ------------------------------------------------------------------- the engine
  /*
   * BUILT IN AN EFFECT, NOT A MEMO: the cleanup disposes it (closing its AudioContext), and strict mode
   * runs every effect's cleanup once before running it again — a memoised engine would be disposed and
   * then reused. Its AudioContext is created lazily (on "Enable audio"), so an engine that is built and
   * thrown away costs nothing.
   */
  const [engine, setEngine] = useState<AudioEngine | null>(null);
  const backendFactory = options.backendFactory;
  useEffect(() => {
    if (!supported) return;
    const backend = backendFactory?.() ?? new WebAudioBackend();
    const built = new AudioEngine({
      backend,
      manifest: LIBRARY,
      format: preferredAudioFormat((mime) => (typeof Audio === 'function' ? new Audio().canPlayType(mime) : 'probably')),
      serverNow: () => channelRef.current?.clock.serverNow(Date.now()) ?? Date.now(),
      events: {
        onChange: setSnapshot,
        onCue: (id) => setLastCue({ id, at: Date.now() }),
        onStale: () => setReloadRequired(true),
        onTick: (sample) => tickSink.current?.(sample),
      },
    });
    setEngine(built);
    return () => {
      built.dispose();
      setEngine((current) => (current === built ? null : current));
      setEnabled(false);
    };
  }, [supported, backendFactory]);

  useEffect(() => {
    engine?.setVolumes(prefs.volumes, prefs.muted);
  }, [engine, prefs.volumes, prefs.muted]);

  // ------------------------------------------------------------------- who is this
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const adopt = (next: AudioIdentity): void => {
      if (cancelled) return;
      // Stats' header is drawn from this too, when it is the first app a tab opens (prefs.ts).
      writePlayerHint(next);
      setIdentity(next);
      setSignedOutReason(null);
      setPhase('ready');
    };
    const checkRenewal = async (reasonIfNone: SignedOutReason): Promise<void> => {
      // No hint and no Discord session: nothing could answer, so nothing is asked.
      if (!hasRenewalHint() && bearerRef.current === null) {
        setPhase('signed-out');
        setSignedOutReason(reasonIfNone);
        return;
      }
      const outcome = await audio.session(bearerRef.current);
      if (cancelled) return;
      if (outcome.ok) {
        writeRenewalHint(outcome.issued.renewalExpiresAt);
        // The renewal re-issues the player session too: Stats read with it (SessionProvider's `self`).
        if (typeof outcome.issued.token === 'string') adoptSelf(outcome.issued.token);
        return adopt(outcome.issued.player);
      }
      if (outcome.reason === 'none') clearRenewalHint();
      setPhase('signed-out');
      setSignedOutReason(outcome.reason === 'unreachable' ? 'unreachable' : reasonIfNone);
    };

    if (code !== null) {
      // Out of the address bar before anything else: `/audio#code=…` becomes `/audio`.
      globalThis.history?.replaceState(null, '', `${globalThis.location.pathname}${globalThis.location.search}`);
      void claimOnce(audio, code).then((outcome) => {
        if (cancelled) return;
        if (outcome.ok) {
          adoptSession(outcome.accepted.token);
          writeRenewalHint(outcome.accepted.renewalExpiresAt);
          adopt(outcome.accepted.player);
        } else {
          // A spent or stale link is not the end: this browser may already hold a renewal.
          void checkRenewal(outcome.reason === 'unreachable' ? 'unreachable' : 'used_or_expired');
        }
      });
    } else {
      void checkRenewal(null);
    }
    return () => {
      cancelled = true;
    };
    // `code` is read once per mount, and the session client is stable for the provider's life.
  }, [active, code, audio, adoptSession, adoptSelf]);

  // ------------------------------------------------------------------- the socket
  const identityUuid = identity?.uuid ?? null;
  useEffect(() => {
    if (phase !== 'ready' || identityUuid === null || engine === null) return;
    const channel = new AudioChannel({
      apiBase: audioApiBase(),
      credential: audio.credentialProvider(
        () => bearerRef.current,
        (issued) => {
          writeRenewalHint(issued.renewalExpiresAt);
          writePlayerHint(issued.player);
          setIdentity(issued.player);
          if (typeof issued.token === 'string') adoptSelf(issued.token);
        },
      ),
      events: {
        onStatus: (status) => {
          setLink(status);
          if (status !== 'open') setHeard(false);
        },
        onMessage: (message) => onMessage(message),
        onReloadRequired: () => setReloadRequired(true),
        onCredentialRefused: () => {
          clearRenewalHint();
          setPhase('signed-out');
          setSignedOutReason('renewal_ended');
        },
      },
    });
    const onMessage = (message: AudioServerMessage): void => {
      switch (message.type) {
        case 'catalog': {
          const { catalog: read, dropped } = normaliseAudioCatalog(message);
          if (dropped.length > 0) console.warn(`audio: the catalog had entries this page cannot use: ${dropped.join('; ')}`);
          if (read === null) return;
          setCatalog(read);
          engine.setCatalog(read);
          return;
        }
        case 'context': {
          const instance = message.instance === undefined ? null : normaliseInstanceRef(message.instance);
          const normalised: AudioContextMessage = {
            type: 'context',
            context: message.context === null ? null : normalisePlayerAudioContext(message.context),
            online: message.online,
            serverTimeMs: message.serverTimeMs,
            ...(instance === null ? {} : { instance }),
          };
          setHeard(true);
          setContext(normalised);
          engine.setContext(normalised);
          return;
        }
        case 'cue':
          engine.cue(message.cue);
          return;
        default:
      }
    };
    channelRef.current = channel;
    channel.start();
    return () => {
      channel.stop();
      if (channelRef.current === channel) channelRef.current = null;
    };
  }, [phase, identityUuid, engine, audio, adoptSelf]);

  // ------------------------------------------------------------- the service worker
  const lang = voiceLanguage(catalog, context?.context ?? null);
  useEffect(() => {
    void postToWorker({ type: 'retain', urls: retainedUrls(LIBRARY, lang) });
  }, [lang]);

  const playing = snapshot.trackId;
  const riding = snapshot.vehicle.trackId;
  useEffect(() => {
    if (!enabled || catalog === null) return;
    // A browser in data-saver mode gets what it plays and nothing ahead of it.
    if ((globalThis.navigator as { connection?: { saveData?: boolean } } | undefined)?.connection?.saveData === true) return;
    const format = preferredAudioFormat((mime) => new Audio().canPlayType(mime));
    // The cues, the ride's sounds while on one, and the hub — nothing else (warmOrder's budget): the whole
    // library is ~112 MB. What either layer is playing already is in the cache by now.
    const ids = warmOrder(catalog, LIBRARY, context?.context ?? null, { playing: [playing, riding].filter((id): id is string => id !== null) });
    const urls = ids.map((id) => LIBRARY[id]).flatMap((entry) => (entry === undefined ? [] : [format === 'ogg' ? entry.ogg || entry.mp3 : entry.mp3 || entry.ogg]));
    // Idle-time only: a little after the current track started, never in its way.
    const timer = setTimeout(() => void postToWorker({ type: 'warm', urls }), 4_000);
    return () => clearTimeout(timer);
  }, [enabled, catalog, context, playing, riding]);

  // ------------------------------------------------------------------- actions
  const enable = useCallback(async () => {
    if (engine === null) return;
    await engine.enable();
    setEnabled(true);
    setPrefs((current) => {
      const next = { ...current, enabledBefore: true };
      writeAudioPrefs(next);
      return next;
    });
  }, [engine]);

  const setVolumes = useCallback((volumes: AudioVolumes) => {
    setPrefs((current) => {
      const next = { ...current, volumes };
      writeAudioPrefs(next);
      return next;
    });
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    setPrefs((current) => {
      const next = { ...current, muted };
      writeAudioPrefs(next);
      return next;
    });
  }, []);

  /** Signed out, by either button: the socket closed, the music faded, the way in shown. */
  const stopForSignOut = useCallback(() => {
    channelRef.current?.stop();
    // Online with no context is "nothing to play here": a fade, with no offline linger to wait out.
    engine?.setContext({ type: 'context', context: null, online: true, serverTimeMs: Date.now() });
    setIdentity(null);
    setPhase('signed-out');
    setSignedOutReason(null);
  }, [engine]);

  // The header's "Sign out" (the provider's) stops this page too.
  const seenSignOuts = useRef(signOuts);
  useEffect(() => {
    if (signOuts === seenSignOuts.current) return;
    seenSignOuts.current = signOuts;
    stopForSignOut();
  }, [signOuts, stopForSignOut]);

  /*
   * "SIGN OUT ON THIS DEVICE" MEANS ALL OF IT. Any sign-in left in this tab would otherwise survive the
   * button and mint a fresh renewal by itself on the next question — a player's session stands in for
   * one, and so, since a staff sign-in finds the player its Discord account is linked to, does a staff
   * one. So the whole provider signs out: the renewal, the hint and the phone's player session go
   * with the sign-in, and the provider's sign-out count stops this page (see `seenSignOuts` above).
   */
  const signOut = useCallback(async () => {
    signOutOfEverything();
  }, [signOutOfEverything]);

  const state: CompanionState = {
    phase,
    identity,
    signedOutReason,
    link,
    heard,
    catalog,
    context,
    snapshot,
    enabled,
    enabledBefore: prefs.enabledBefore,
    reloadRequired,
    lastCue,
    prefs,
    supported,
  };
  const staff = session !== null && isStaff(session.claims.role);
  const clock = () => {
    const channel = channelRef.current;
    return channel === null ? null : { offsetMs: channel.offsetMs, roundTripMs: channel.clock.roundTripMs, samples: channel.clock.sampleCount };
  };
  const position = () => engine?.position() ?? null;
  return { state, staff, enable, setVolumes, setMuted, signOut, clock, position, tickSink, engine };
}

/** The sidecar's origin for the socket and the way in: the same base every API call uses. */
function audioApiBase(): string {
  return (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';
}

/** Posts to the active service worker, if this build registered one. Best effort, never throws. */
async function postToWorker(message: { type: 'warm' | 'retain'; urls: readonly string[] }): Promise<void> {
  try {
    const worker = globalThis.navigator?.serviceWorker;
    if (worker === undefined) return;
    const registration = await worker.getRegistration();
    registration?.active?.postMessage(message);
  } catch {
    /* no worker in dev, in a private window, or in a browser without one: nothing is warmed */
  }
}
